import * as THREE from 'three';
import EventEmitter from 'eventemitter3';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader';
import CrucibleData from '../crucibleData';
import CrucibleGroup from './crucibleGroup';
import CrucibleMetadata from './crucibleMetadata';
import CameraAnimator from './cameraAnimator';
import CrucibleSceneState from './crucibleSceneState';
import config from './config.json';
import PostProcessingManager from './postProcessingManager';
import PRNG from './PRNG';
import { isMobile } from 'react-device-detect';

export default class CrucibleScene {
  public scene: THREE.Scene;
  public options: CrucibleSceneState;

  public crucible: CrucibleGroup;
  public raycaster: THREE.Raycaster;
  public data: CrucibleData | undefined;

  public isLidOpen: boolean;
  public isVisible: boolean;
  public isRendering: boolean;
  public isLoaded: boolean;

  readonly domElement: HTMLCanvasElement;
  readonly ee: EventEmitter;

  protected _camera: THREE.PerspectiveCamera;
  protected _renderer: THREE.WebGLRenderer;
  protected _postProcessingManager: PostProcessingManager;

  protected _controls: OrbitControls;
  protected _timer: THREE.Clock;
  protected _clickPos: THREE.Vector2;
  protected _cameraAnimator: CameraAnimator;
  protected _backgroundColor: string;

  protected _ambientLight: THREE.AmbientLight;
  protected _dynamicLights: THREE.Light[];

  protected _boundAnimate: () => void;
  protected _boundOnWindowResize: () => void;
  protected _boundPlayOnClick: (e: MouseEvent) => void;

  constructor() {
    this.scene = new THREE.Scene();
    this.ee = new EventEmitter();
    this.data = undefined;

    this.raycaster = new THREE.Raycaster();
    this._timer = new THREE.Clock();

    this.isLidOpen = false;
    this.isVisible = false;
    this.isRendering = false;
    this.isLoaded = false;

    // Scene Options default values
    this.options = {
      idTextVisible: true,
      screenshotMode: false,
      shadows: false,
      dynamicLights: false,
      bloom: true,
      antialias: false,
      shadowMapSize: 512,
      hdri: config.sceneConfig.hdriUrl,
      toneMapping: THREE.ACESFilmicToneMapping,
      textureQuality: isMobile
        ? config.sceneConfig.quality['low'].name
        : config.sceneConfig.quality['medium'].name,
      background: true,
      postProcessing: true
    };

    // Renderer settings
    this._renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true
    });

    this._renderer.physicallyCorrectLights = true;
    this._renderer.setClearColor(0x18112e, 0.0);
    this._renderer.autoClear = false;
    this._renderer.toneMapping = this.options.toneMapping;
    this._renderer.toneMappingExposure = 1;
    this._renderer.shadowMap.enabled = this.options.shadows;
    this._renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.domElement = this._renderer.domElement;
    this._backgroundColor = 'hsl(260, 60%, 10%)';

    this.crucible = new CrucibleGroup(
      this._renderer,
      this.options.idTextVisible,
      this
    );

    // Camera
    this._camera = new THREE.PerspectiveCamera(
      60,
      this.domElement.clientWidth / this.domElement.clientHeight,
      0.1,
      4000
    );
    this._camera.position.set(-14, 20, 20);
    this._camera.lookAt(new THREE.Vector3(-10, 0, 0));

    // Camera controls
    this._controls = new OrbitControls(this._camera, this.domElement);
    this._controls.screenSpacePanning = true;
    this._controls.enableDamping = true;
    this._controls.dampingFactor = 0.05;
    this._controls.minDistance = 0.5;
    this._controls.maxDistance = 80;

    // Camera Animator
    this._cameraAnimator = new CameraAnimator(
      this._camera,
      this,
      this._controls
    );

    // Lights
    const ambientIntensity = this.options.postProcessing ? 0.7 : 1.2;
    this._ambientLight = new THREE.AmbientLight(0xffffff, ambientIntensity);
    this.scene.add(this._ambientLight);
    this._dynamicLights = [];

    // HDRI environment lighting
    this.loadEnvironmentMap().catch(() => {
      console.log('Retry Load EXR');
      this.loadEnvironmentMap();
    });

    // Post-processing
    this._postProcessingManager = new PostProcessingManager(
      this._renderer,
      this.scene,
      this._camera
    );

    // Binding events and animation
    this._boundAnimate = this._animate.bind(this);
    this._boundOnWindowResize = this.resizeCanvas.bind(this);
    this._boundPlayOnClick = this._playOnClick.bind(this);

    this._controls.addEventListener('start', (e) => {
      this.ee.emit('camera', { type: 'custom' });
    });

    this._clickPos = new THREE.Vector2();

    this.domElement.addEventListener('pointerdown', (e: MouseEvent) => {
      this._clickPos.x = e.clientX;
      this._clickPos.y = e.clientY;
    });

    this.domElement.addEventListener('pointerup', (e: MouseEvent) => {
      if (this._clickPos.x === e.clientX && this._clickPos.y === e.clientY) {
        return this._boundPlayOnClick(e);
      }
    });

    window.addEventListener('resize', this._boundOnWindowResize, false);

    this.resizeCanvas();
  }

  async loadEnvironmentMap() {
    const exrLoader = new EXRLoader();
    exrLoader.setDataType(THREE.UnsignedByteType);

    const envTexture = await exrLoader.loadAsync(config.sceneConfig.hdriUrl);

    const pmrem = new THREE.PMREMGenerator(this._renderer);
    pmrem.compileEquirectangularShader();
    const envMap = pmrem.fromEquirectangular(envTexture).texture;
    this.scene.environment = envMap;

    pmrem.dispose();
    envTexture.dispose();
  }

  async loadCrucible(metadata: CrucibleMetadata) {
    this.isLoaded = false;
    this.scene.remove(this.crucible.group);
    this.crucible.group = new THREE.Group();
    this.crucible.group.name = `Crucible ${metadata.crucibleId}`;
    this.scene.add(this.crucible.group);

    if (this.options.screenshotMode) {
      this._renderer.setClearColor(0x070312);
      this._renderer.setClearAlpha(1.0);
    }

    this._cameraAnimator.resetCamera();

    this.ee.emit('load', { type: 'initiated' });

    this.crucible
      .load(metadata, this.options.idTextVisible, this.options.textureQuality)
      .then(() => {
        if (this.scene.environment === null) {
          console.log('EXR/HDRI Failed to load');
          this.ee.emit('load', { type: 'failed' });
          return;
        }
        this.ee.emit('load', { type: 'completed' });
        this.isLoaded = true;

        this._backgroundColor = this.crucible.colors.getHSLColor();
        this.crucible.renderCruciblePieces(this.options.screenshotMode);

        if (this.data !== undefined) this.update(this.data, metadata);

        if (this.options.screenshotMode) {
          this.crucible.openShellInstantly();
          this.isLidOpen = true;
          this.ee.emit('lid', { type: 'open' });
        } else {
          setTimeout(() => {
            this.isLidOpen = false;
            this.openLidAnimation();
            this.ee.emit('lid', { type: 'open' });
          }, 1500);
        }
      })
      .catch(() => {
        this.ee.emit('load', { type: 'failed' });
        return;
      });
  }

  update(crucibleData: CrucibleData, metadata: CrucibleMetadata) {
    // Whenever the data is loaded, store a reference here, but only update the model if it's already loaded
    if (crucibleData !== undefined && crucibleData.id === metadata.crucibleId) {
      this.data = crucibleData;
      if (this.isLoaded)
        this.crucible.update(crucibleData, metadata, this.scene);
    }
    this.resizeCanvas();
  }

  clear() {
    this.isLoaded = false;
    this.scene.remove(this.crucible.group);
    this._dispose(this.crucible.group);

    let sceneObjs: THREE.Object3D[] = [];
    this.scene.traverse((node) => {
      if (node instanceof THREE.Object3D) sceneObjs.push(node);
    });

    sceneObjs.forEach((node) => {
      // Don't remove Lights from the scene when it's cleared
      if (node instanceof THREE.Light) return;
      this.scene.remove(node);
      this._dispose(node);
    });
  }

  _dispose(object: THREE.Object3D) {
    if (object === undefined) return;
    object.traverse((node) => {
      // Dispose of all meshes
      if (node instanceof THREE.Mesh) {
        node.geometry.dispose();
        // node.geometry = undefined;

        const materials = Array.isArray(node.material)
          ? node.material
          : [node.material];
        materials.forEach((material) => {
          if (node.material.map) node.material.map.dispose();
          if (node.material.emissiveMap) node.material.emissiveMap.dispose();
          if (node.material.metalnessMap) node.material.metalnessMap.dispose();
          if (node.material.normalMap) node.material.normalMap.dispose();
          if (node.material.specularMap) node.material.specularMap.dispose();
          if (node.material.metalnessMap) node.material.metalnessMap.dispose();
          if (node.material.glossinessMap)
            node.material.glossinessMap.dispose();
          if (node.material.aoMap) node.material.aoMap.dispose();
          if (node.material.envMap) node.material.envMap.dispose();
          material.dispose();
          // material = undefined;
        });
      }
      // Dispose of all sprites
      if (node instanceof THREE.Sprite) {
        node.geometry.dispose();
        node.material.map?.dispose();
        node.material.dispose();
      }
    });
  }

  openLidAnimation() {
    if (!this.isLidOpen) {
      this.crucible.playKeyframeAnimations();
      this.isLidOpen = true;
      this.ee.emit('lid', { type: 'open' });
    } else {
      this.crucible.playKeyframeAnimationsReversed();
      this.isLidOpen = false;
      this.ee.emit('lid', { type: 'close' });
    }
  }

  getBackgroundColor(prng: PRNG) {
    // returning the CSS to be added to the domElement.style.background
    return (
      'radial-gradient(circle, ' +
      this._backgroundColor +
      ', hsl(260, 60%, 5%))'
    );
  }

  startRendering() {
    if (!this.isVisible) {
      this.isVisible = true;
      if (!this.isRendering) this._boundAnimate();
    }
  }

  stopRendering() {
    this.isVisible = false;
  }

  selectCamera(cameraName: string) {
    if (cameraName === 'custom') return;
    this._cameraAnimator.changeSelectedCamera(cameraName);
  }

  resizeCanvas() {
    this.domElement.style.width = '100%';
    this.domElement.style.width = '100%';

    if (this.domElement.parentElement) {
      // FPS Counter used for testing only
      // TODO: Add to dev-only environment
      // fpsCounter.addToScene(this.domElement.parentElement);

      const width = this.domElement.parentElement?.offsetWidth;
      const height = this.domElement.parentElement?.offsetHeight;

      this._camera.aspect = width / height;
      this._camera.updateProjectionMatrix();

      this._renderer.setSize(width, height);
      this._postProcessingManager.resize(
        width,
        height,
        this._renderer.getPixelRatio()
      );
    }
  }

  changeTextureQuality(textureQuality: string) {
    this.options.textureQuality = textureQuality;
    this.crucible.loader.changeTextureQuality(textureQuality);
    this.ee.emit('settings', { type: 'textureQuality' });
  }

  changeGraphicsSettings(setting: { type: string; value: any }) {
    switch (setting.type) {
      case 'lights': {
        if (setting.value === true) {
          this._addDynamicLights();
        } else {
          this._removeDynamicLights();

          if (this.options.shadows) {
            this.options.shadows = false;
            this.ee.emit('settings', { type: 'shadows' });
          }
        }

        this.ee.emit('settings', { type: 'lights' });
        break;
      }

      case 'shadows': {
        if (setting.value === true) {
          this._addShadows();
          this.options.dynamicLights = true;
          this.ee.emit('settings', { type: 'lights' });
        } else {
          this._removeShadows();
        }

        this.ee.emit('settings', { type: 'shadows' });
        break;
      }

      case 'antialias': {
        if (setting.value === true) {
          this.options.antialias = true;

          this._postProcessingManager.addFXAAPass(
            this._renderer.domElement.offsetWidth,
            this._renderer.domElement.offsetHeight,
            this._renderer.getPixelRatio()
          );
        } else {
          this.options.antialias = false;
          this._postProcessingManager.removeFXAAPass();
        }

        this.ee.emit('settings', { type: 'antialias' });
        break;
      }

      case 'bloom': {
        if (setting.value === true) {
          this.options.bloom = true;
          this._postProcessingManager.addBloomPass(
            this._renderer.domElement.offsetWidth,
            this._renderer.domElement.offsetHeight
          );
        } else {
          this.options.bloom = false;
          this._postProcessingManager.removeBloomPass();
        }

        this.ee.emit('settings', { type: 'bloom' });
        break;
      }
    }
  }

  _addDynamicLights() {
    if (this.options.dynamicLights) {
      return;
    }

    this.options.dynamicLights = true;

    // Add directional light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.7);
    directionalLight.position.set(0, 400, -50);
    directionalLight.castShadow = this.options.shadows;
    directionalLight.shadow.mapSize = new THREE.Vector2(
      this.options.shadowMapSize,
      this.options.shadowMapSize
    );

    directionalLight.shadow.camera.left = -200;
    directionalLight.shadow.camera.right = 200;
    directionalLight.shadow.camera.top = 200;
    directionalLight.shadow.camera.bottom = -200;
    directionalLight.shadow.camera.near = 0;
    directionalLight.shadow.camera.far = 500;

    directionalLight.shadow.bias = 0.000001;

    // Add hemisphere light
    // const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x353535, 0.6);

    this._dynamicLights.push(directionalLight);
    this.scene.add(directionalLight);
  }

  _removeDynamicLights() {
    if (!this.options.dynamicLights) {
      return;
    }

    this.options.dynamicLights = false;

    this._dynamicLights.forEach((light) => {
      this.scene.remove(light);
      this._dispose(light);
    });

    this._dynamicLights = [];
  }

  _addShadows() {
    if (this.options.shadows) return;

    this.options.shadows = true;
    this._addDynamicLights();

    this._dynamicLights.forEach((light) => {
      light.castShadow = true;
    });

    this._renderer.shadowMap.enabled = true;
    this._updateMaterials();
  }

  _removeShadows() {
    if (!this.options.shadows) return;

    this.options.shadows = false;
    this._renderer.shadowMap.enabled = false;

    this._renderer.shadowMap.autoUpdate = false;

    this._dynamicLights.forEach((light) => {
      light.castShadow = false;
    });

    this._updateMaterials();
    this._renderer.shadowMap.autoUpdate = true;
  }

  _updateMaterials() {
    this.scene.traverse((node) => {
      if (node instanceof THREE.Mesh && node.material) {
        node.material.needsUpdate = true;
      }
    });
  }

  _animate() {
    if (this.isVisible) {
      const timeElapsed = this._timer.getDelta();
      this.crucible.animate(timeElapsed, this.options.screenshotMode);
      this._cameraAnimator.animate(timeElapsed);
      this._controls.update();
      this._postProcessingManager.render();

      requestAnimationFrame(this._boundAnimate);
      this.isRendering = true;

      // fpsCounter.countFPS();
    } else {
      this.isRendering = false;
    }
  }

  _playOnClick(event: MouseEvent) {
    if (this.domElement.parentElement) {
      const pointer = new THREE.Vector2();
      const rect = this._renderer.domElement.getBoundingClientRect();

      pointer.x =
        ((event.clientX - rect.left) / this.domElement.clientWidth) * 2 - 1;
      pointer.y =
        ((event.clientY - rect.top) / this.domElement.clientHeight) * -2 + 1;

      this.raycaster.setFromCamera(pointer, this._camera);
      this.crucible.playOnClick(this.raycaster);
    }
  }
}
